import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { ErrorPanel } from '@/components/px/ErrorPanel'
import { ForbiddenPanel } from '@/components/px/ForbiddenPanel'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill } from '@/components/px/StatusPill'
import { selectActiveOrgId } from '@/features/auth/slices/sessionSlice'
import { epochLabel } from '@/features/projects/format'
import { useAppDispatch, useAppSelector } from '@/store'
import {
  loadCaptureSurface,
  selectActiveIntakeVersionId,
  selectCaptureForbidden,
  selectCaptureLinksError,
  selectCaptureLoading,
  selectCaptureSurface,
  selectChannelsError,
  selectExternalCapturesError,
  selectIntakesNonce
} from '../slices/intakesSlice'
import type { KeyedFeatureError } from '../types'

/**
 * The org-admin meeting-capture surface (AC-3/AC-6): per provider, whether a
 * credential exists and is active plus its non-secret metadata, whether the
 * participant notice has been recorded, the unrouted-capture pool, and the
 * version's capture links. Renders NO secret material of any kind.
 *
 * The capture surface is a SEPARATE sub-state with its own error field
 * (decision 3): a 403 (a non-admin caller — `MeetingChannelController` is
 * `@Authenticated(orgAdmin = true)` at the class level) renders
 * `ForbiddenPanel` IN this panel only; it never becomes the page's blocked
 * state, so the rest of the Interviews page keeps working.
 *
 * PX-W7.1 fix cycle 1 (GRP-B3D9F416, findings 3b/4): below the 403 branch,
 * the THREE reads (channels, external-capture pool, capture links) are
 * rendered as three INDEPENDENT blocks. A non-403 failure on one — capture
 * links currently 400s server-side (`project.id.invalid`), see the CR posted
 * on TASK-9B7FB7CC — renders that block's own `ErrorPanel` and never hides
 * the other two, which is the whole point of `Promise.allSettled` upstream: a
 * single shared error used to fold every read into one blanked panel even
 * when two of the three had actually succeeded. The capture-links block is
 * additionally a TRI-STATE: `null` (not read for this version yet, either no
 * version is known or the read is still pending/failed) renders honest
 * "not read yet" copy, never the zero-count sentence a `[]` earns once a real
 * read has actually completed.
 */
export function CaptureChannelsPanel() {
  const { t } = useTranslation('projectWorkspace')
  const dispatch = useAppDispatch()
  const orgId = useAppSelector(selectActiveOrgId)
  const { projectId } = useParams()
  const activeVersionId = useAppSelector(selectActiveIntakeVersionId)
  const nonce = useAppSelector(selectIntakesNonce)

  const capture = useAppSelector(selectCaptureSurface)
  const loading = useAppSelector(selectCaptureLoading)
  const channelsError = useAppSelector(selectChannelsError)
  const externalCapturesError = useAppSelector(selectExternalCapturesError)
  const captureLinksError = useAppSelector(selectCaptureLinksError)
  const forbidden = useAppSelector(selectCaptureForbidden)

  useEffect(() => {
    if (orgId === null || projectId === undefined) return
    void dispatch(loadCaptureSurface({ orgId, projectId, versionId: activeVersionId }))
  }, [dispatch, orgId, projectId, activeVersionId, nonce])

  if (orgId === null || projectId === undefined) return null

  const onRetryClicked = (): void => {
    void dispatch(loadCaptureSurface({ orgId, projectId, versionId: activeVersionId }))
  }

  // Defensive reads: never assume the object shape — a malformed or
  // still-loading payload must render the empty state, never throw.
  const providers = capture?.channels?.providers ?? []
  const credentials = capture?.channels?.credentials ?? []
  const noticeAcknowledgedAt = capture?.channels?.noticeAcknowledgedAt ?? null
  const externalCaptures = capture?.externalCaptures ?? []
  // Tri-state (finding 4): `null` while unread FOR THIS VERSION; an array
  // (possibly empty) only once a real read has completed.
  const captureLinks = capture?.captureLinks ?? null

  const asErrorPanelProps = (failure: KeyedFeatureError) => ({
    status: failure.status,
    backendKey: failure.code,
    message: failure.message
  })

  return (
    <section aria-label={t('intakes.capture.panelTitle')} className="rounded-lg border border-border bg-card px-4 py-3.5">
      <SectionLabel>{t('intakes.capture.panelTitle')}</SectionLabel>
      <div className="mt-2">
        {forbidden ? (
          <ForbiddenPanel requested={t('intakes.capture.panelTitle')} />
        ) : loading && capture === null ? (
          <LoadingPanel label={t('loading')} rows={2} />
        ) : (
          <div data-testid="capture-surface">
            {/* Provider credentials — its own failed state (finding 3b). */}
            {channelsError !== null ? (
              <ErrorPanel error={asErrorPanelProps(channelsError)} onRetryClicked={onRetryClicked} />
            ) : (
              <>
                <p className="text-xs text-muted-foreground">
                  {noticeAcknowledgedAt !== null
                    ? t('intakes.capture.noticeRecorded', { date: epochLabel(noticeAcknowledgedAt) })
                    : t('intakes.capture.noticeMissing')}
                </p>
                <ul className="mt-2 grid gap-2 sm:grid-cols-2">
                  {providers.map((provider) => {
                    const credential = credentials.find((row) => row.provider === provider)
                    const active = credential?.active === true
                    return (
                      <li key={provider} className="rounded-md border border-border px-3 py-2">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-medium text-foreground">{provider}</span>
                          <StatusPill tone={active ? 'done' : 'neutral'}>
                            {active ? t('intakes.capture.credentialActive') : t('intakes.capture.credentialNone')}
                          </StatusPill>
                        </div>
                        {credential?.subject !== undefined && (
                          <p className="mt-1 text-xs text-muted-foreground">{credential.subject}</p>
                        )}
                      </li>
                    )
                  })}
                </ul>
              </>
            )}

            {/* Unrouted capture pool — its own failed state (finding 3b). */}
            <p className="mt-3 text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">
              {t('intakes.capture.poolTitle', { count: externalCaptures.length })}
            </p>
            {externalCapturesError !== null ? (
              <ErrorPanel className="mt-1" error={asErrorPanelProps(externalCapturesError)} onRetryClicked={onRetryClicked} />
            ) : externalCaptures.length === 0 ? (
              <p className="mt-1 text-xs text-muted-foreground">{t('intakes.capture.poolEmpty')}</p>
            ) : (
              <ul className="mt-1 space-y-1 text-xs text-muted-foreground">
                {externalCaptures.map((row) => (
                  <li key={row.id}>{row.id}</li>
                ))}
              </ul>
            )}

            {/* Capture links — its own failed state (finding 3b) + tri-state (finding 4).
                The heading is the SAME element in all three arms, so it is rendered once:
                only a completed, error-free read (an array) may carry the count; an unread
                or failed read says so instead of implying a counted zero. */}
            <p className="mt-3 text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">
              {captureLinksError !== null || captureLinks === null
                ? t('intakes.capture.linksTitleUnread')
                : t('intakes.capture.linksTitle', { count: captureLinks.length })}
            </p>
            {captureLinksError !== null ? (
              <ErrorPanel className="mt-1" error={asErrorPanelProps(captureLinksError)} onRetryClicked={onRetryClicked} />
            ) : captureLinks === null ? (
              <p className="mt-1 text-xs text-muted-foreground">{t('intakes.capture.linksUnread')}</p>
            ) : captureLinks.length === 0 ? (
              <p className="mt-1 text-xs text-muted-foreground">{t('intakes.capture.linksEmpty')}</p>
            ) : (
              <ul className="mt-1 space-y-1 text-xs text-muted-foreground">
                {captureLinks.map((row) => (
                  <li key={row.id}>
                    {row.provider ?? row.id} — {row.status ?? '—'}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </section>
  )
}
